import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { BeneficiaryState } from '@app/finxone-web-frontend/app/lib/state/beneficiary.state';
import { countryCodeToCircleFlag } from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-to-flag.utils';
import { kebabToCamelCase } from '@app/finxone-web-frontend/app/lib/utils/utils';
import {
  APP_ZONES,
  BaseCssProperties,
  BeneficiaryBankAddressRequirements,
  PaymentFlowTypes,
  ProviderClass,
  State,
  WidgetNames,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
  cssToNgStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { debounceTime, map, takeUntil } from 'rxjs';

import { Router } from '@angular/router';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { FormActionTypeEnum, OtpParam } from '@finxone-platform/form-action';
import { IconCacheService } from '@finxone-platform/shared/services';
import Handlebars from 'handlebars';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../lib/services/multipart-holder/multipart-form-holder.service';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { FormSubmitter } from '../form-widgets/form-submitter/form-submitter.component';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';
const showStateDropdown = ['US', 'MX', 'CA'];

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-intl-beneficiary-address',
  templateUrl: './intl-beneficiary-address.component.html',
})
export class IntlBeneficiaryAddressComponent extends FormSubmitter implements OnInit {
  @Output() handleAddressChange = new EventEmitter<any>();
  @Input() editAddress = false;
  @Input() editBeneficiarySepa = false;
  @Input() formActionData: any;
  public showNestedButton = false;
  public selectedStateOption: State | undefined;
  public selectedCountryOption: { title: string; currencyFlag: string; key: string };
  public buttonContent = '';
  public renderedButtonContent = '';
  public zone = '';
  public nextPageUrl: string;
  public addressForm: FormGroup;
  public countries: Country[];
  public countryList: { title: string; currencyFlag: string; key: string }[];
  public reviewDetailsButtonStyle: baseCssConfigStyle = {};
  public states: State[];
  public isShowStateDropdown = false;
  public requirements: BeneficiaryBankAddressRequirements | undefined;
  public fieldsList: {
    label: string;
    title: string;
    item: { regex?: RegExp; requirement: string };
    formControlName: string;
    validated?: boolean;
  }[] = [];
  public textInputFontStyle: baseCssConfigStyle;
  public currentActiveStyle: baseCssConfigStyle;
  public currentInactiveStyle: baseCssConfigStyle;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public paymentFlow: string | null;
  public enableAddress2 = true;
  public enableAddress3 = true;
  public enableAddress4 = true;
  private addressfieldMap = new Map<string, string>([
    ['addressLine1', 'addressLine1'],
    ['addressLine2', 'addressLine2'],
    ['addressLine3', 'addressLine3'],
    ['addressLine4', 'addressLine4'],
    ['city', 'addressCity'],
    ['country', 'addressCountry'],
    ['region', 'addressState'],
    ['postalCode', 'addressPostCode'],
  ]);
  public renderedContent = '';

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    private configService: ConfigService,
    public override store: Store,
    public formHolderService: MultiPartFormHolderService,
    private formSubmissionService: FormSubmissionService,
    private urlZoneInput: ZonePageUrlProvider,
    private formBuilder: FormBuilder,
    private router: Router,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private accountService: AccountService,
  ) {
    super(store);
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BORDER,
      BaseCssProperties.COLOR,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);
    this.textInputFontStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.TEXT_INPUT_FONT_SIZE,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);

    this.configService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((response: Country[]) => {
          this.countries = response;
          this.countryList = [
            {
              title: '',
              currencyFlag: '',
              key: '',
            },
            ...this.countries.map(({ name, code }) => ({
              title: name,
              currencyFlag: this.getCountryFlag(code, '24px'),
              key: code,
            })),
          ];
        }),
      )
      .subscribe();
    this.zone = this.urlZoneInput.zoneUrlInput;
  }

  ngOnInit(): void {
    try {
      const queryParams = new URLSearchParams(this.router.url?.split('?')[1] ?? '');
      this.paymentFlow = queryParams?.get('paymentflow');
      if (this.paymentFlow === PaymentFlowTypes.SEPA) {
        this.showNestedButton = true;
      }
      this.onGetBeneficiaryRequiredFields();
      this.stateUpdatesListener();
      this.onLoadWidgetContent();
      if (this.dataIn.widgetConfig?.nestedWidgets?.['reviewDetailsButton'])
        this.reviewDetailsButtonStyle = this.configService.getSpecificBaseCssStyle(
          this?.getNestedWidgetConfig(this.dataIn, 'reviewDetailsButton'),
          buttonNestedWidgetConfigStyles,
        );
      this.enableAddress2 = this.dataIn.widgetConfig.attributes.widgetProperties?.['enableAddress2'] ?? true;
      this.enableAddress3 = this.dataIn.widgetConfig.attributes.widgetProperties?.['enableAddress3'] ?? true;
      this.enableAddress4 = this.dataIn.widgetConfig.attributes.widgetProperties?.['enableAddress4'] ?? true;
      this.getGlobalStyling();
    } catch (error) {
      console.error(error);
    }
  }
  private onLoadWidgetContent(): void {
    this.buttonContent =
      this.dataIn.widgetConfig?.nestedWidgets?.['reviewDetailsButton']?.widgetProperties?.textContent ??
      'Review Beneficiary Details';
    const template = Handlebars.compile(this.buttonContent);
    buildHandleBarModel(
      this.store,
      this.buttonContent,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton']?.widgetProperties?.[
        'globalStylingClass'
      ] &&
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton']?.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton']?.widgetProperties?.[
          'globalStylingClass'
        ] ?? '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton']?.widgetProperties?.[
          'globalStyling'
        ] ?? false;
    }
  }

  private patchValuesFromFormActionState(formActionData: any) {
    this.addressForm.patchValue({
      addressCountry: formActionData.country,
      addressLine1: formActionData?.addressLine1,
      addressLine2: formActionData?.addressLine2,
      addressLine3: formActionData?.addressLine3,
      addressLine4: formActionData?.addressLine4,
      addressCity: formActionData.city,
      addressPostCode: formActionData?.postalCode ?? formActionData?.postCode,
    });
    this.valueChangeCountry(formActionData);
    this.emitAddressChange();
  }

  private emitAddressChange() {
    const formValues = this.addressForm.value;
    const payload = {
      address: {
        country: formValues?.addressCountry,
        addressLine1: formValues?.addressLine1,
        addressLine2: formValues?.addressLine2,
        addressLine3: formValues?.addressLine3,
        addressLine4: formValues?.addressLine4,
        city: formValues?.addressCity,
        postCode: formValues?.addressPostCode,
        state: formValues?.addressState,
      },
      isAddressFormValid: this.addressForm.valid,
    };
    this.handleAddressChange.emit(payload);
  }

  private findCountryByKeyAndAssignValue(countryInput: string): any {
    return this.countryList?.find((country) => country?.key === countryInput);
  }

  private listenToFormValueChanges() {
    this.addressForm.valueChanges
      .pipe(
        debounceTime(600),
        takeUntil(this.destroy$),
        map(() => {
          this.emitAddressChange();
        }),
      )
      .subscribe();
  }

  public getCountryFlag(countryCode: string, widthValue: string): string {
    return countryCodeToCircleFlag(countryCode, widthValue);
  }

  private stateUpdatesListener(): void {
    this.subscribeToFormAction().subscribe((response) => {
      this.nextPageUrl =
        response?.nextPageUrl !== '' && response?.nextPageUrl
          ? response?.nextPageUrl
          : '/zones/payments/intl-review-beneficiary-details';
      if (response?.type === 'submitStep') this.onSubmit();
    });
  }

  private onGetBeneficiaryRequiredFields() {
    this.showNestedButton = true;
    this.store
      .select(BeneficiaryState.getBeneficiaryRequiredFields())
      .pipe(
        takeUntil(this.destroy$),
        map((requiredFieldResponse) => {
          if (!requiredFieldResponse?.beneficiaryAddress) {
            return;
          }
          // new requirements need to reset
          this.fieldsList = [];
          this.requirements = requiredFieldResponse?.beneficiaryAddress;
          const widgetProperties = this.dataIn.widgetConfig.attributes.widgetProperties;
          // new requirements need to reset
          this.fieldsList = [];
          for (const [key, value] of Object.entries(this.requirements as object)) {
            for (const widgetKey in widgetProperties) {
              if (Object.hasOwn(widgetProperties, widgetKey)) {
                const widgetLabel = widgetProperties[widgetKey];
                const formattedKey = kebabToCamelCase(widgetKey)?.toLowerCase();
                if (
                  formattedKey?.includes(key?.toLowerCase()) ||
                  (formattedKey?.includes('postal') && key?.toLowerCase()?.includes('postal'))
                ) {
                  if (!this.fieldsList.some((field) => field.title === key)) {
                    this.fieldsList.push({
                      label: widgetLabel,
                      title: key,
                      item: value,
                      formControlName: this.getFormControlName(key),
                    });
                  }
                  this.addOptionalAddressFields(['addressLine2', 'addressLine3', 'addressLine4']);
                }
              }
            }
          }
          this.generateForm();
        }),
      )
      .subscribe();
  }

  private generateForm() {
    this.addressForm = this.formBuilder.group({});
    if (this.fieldsList?.length > 0) {
      for (const field of this.fieldsList) {
        const validators = [];
        if (field.item.requirement === 'required') {
          validators.push(Validators.required);
        }
        if (field.item.regex && field.formControlName !== 'addressCountry') {
          validators.push(Validators.pattern(field.item.regex));
        }

        if (field.item.requirement !== 'none' && field.formControlName !== '') {
          this.addressForm.addControl(field.formControlName, new FormControl('', validators));
        }
      }
    }

    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.INTL_BENEFICIARY_ADDRESS,
      route: '',
      form: this.addressForm,
    });

    this.formHolderService.config.addStage(formStage);
    this.onCheckFormIsExist(formStage);
    this.buildForm();
    if (this.addressForm && (this.editAddress || this.editBeneficiarySepa)) {
      this.showNestedButton = false;
      this.patchValuesFromFormActionState(this.formActionData);
      this.listenToFormValueChanges();
    }
  }

  private getFormControlName(fieldName: string): string {
    return this.addressfieldMap.get(fieldName) || '';
  }

  private addOptionalAddressFields(addressLines: string[]) {
    addressLines.forEach((addressLine, index) => {
      if (!this.fieldsList.some((item) => item.formControlName === addressLine)) {
        this.fieldsList.push({
          label: `Address line ${index + 2}`,
          title: addressLine,
          item: { requirement: 'optional' },
          formControlName: addressLine,
        });
      }
    });
  }

  onCheckFormIsExist(formStage: FormStage) {
    const isFormControlExist = this.formSubmissionService.currentPageFormGroups.every((item) =>
      item.get('addressCountry'),
    );

    if (!isFormControlExist) {
      this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    }
  }

  private buildForm(): void {
    const stage = this.formHolderService.config.getStage(WidgetNames.INTL_BENEFICIARY_ADDRESS);
    this.addressForm = stage.getForm();
  }

  public onSubmit(): void {
    if (this.addressForm.valid) {
      const formValues: Record<string, any> = this.addressForm.value;
      if (
        formValues['addressCountry'] &&
        typeof formValues['addressCountry'] === 'object' &&
        'key' in formValues['addressCountry']
      ) {
        const countryKey = formValues['addressCountry']['key'];
        formValues['addressCountry'] = countryKey;
      }
      if (this.paymentFlow === PaymentFlowTypes.SEPA) {
        this.formSubmissionService
          .submitIndividualForm(formValues, '', FormActionTypeEnum.ADD_BENEFICIARY)
          .then((success) => {
            if (success) {
              let name = '';
              Object.values(formValues).forEach((value: string) => {
                name = name + value + ' ';
              });
            } else {
              return;
            }
          });
        this.router.navigateByUrl(
          `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-match?flow=${
            OtpParam.AddBeneficiary
          }&paymentflow=${PaymentFlowTypes.SEPA}`,
        );
      } else if (this.paymentFlow === PaymentFlowTypes.UK_FASTER_PAYMENTS) {
        this.formSubmissionService
          .submitIndividualForm(formValues, '', FormActionTypeEnum.ADD_BENEFICIARY)
          .then((success) => {
            if (success) {
              let name = '';
              Object.values(formValues).forEach((value: string) => {
                name = name + value + ' ';
              });
            } else {
              return;
            }
          });
        this.router.navigateByUrl(
          `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-match?flow=${
            OtpParam.AddBeneficiary
          }&paymentflow=${PaymentFlowTypes.UK_FASTER_PAYMENTS}`,
        );
      } else {
        this.formSubmissionService
          .submitIndividualForm(formValues, this.nextPageUrl, FormActionTypeEnum.INTL_BENEFICIARY_ADD)
          .then((success) => {
            if (success) {
              let name = '';
              Object.values(formValues).forEach((value: string) => {
                name = name + value + ' ';
              });
            } else {
              return;
            }
          });
      }
    } else {
      this.addressForm.markAllAsTouched();
    }
  }
  valueChangeCountry(formActionData?: any) {
    this.selectedCountryOption = this.findCountryByKeyAndAssignValue(this.addressForm.value.addressCountry);
    this.addressForm.get('addressState')?.setValue('');
    const selCountryCode = this.addressForm.value.addressCountry;
    this.isShowStateDropdown = false;
    this.states = [];
    if (
      selCountryCode &&
      showStateDropdown.find((code) => code.toLowerCase() == selCountryCode.toLowerCase())
    ) {
      this.configService.getStates(selCountryCode).subscribe((res: State[]) => {
        this.states = res;
        if (formActionData?.state) {
          const stateValue = this.states.find((v) => v.state_code === formActionData.state);
          this.addressForm.get('addressState')?.patchValue(stateValue?.state_code);
          if (stateValue) this.selectedStateOption = stateValue ?? '';
        }
      });
      this.isShowStateDropdown = true;
    } else {
      this.addressForm.get('addressState')?.patchValue(formActionData?.state);
      this.selectedStateOption = formActionData?.state ?? '';
    }
  }
  public get nestedButtonParentCss() {
    const nestedWidgetAttri = this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'];
    return cssToNgStyle(nestedWidgetAttri?.['nestedButtonParentCss'] ?? '');
  }
}
